/**
 * 主題詳細頁 — 兩個 Tab：旗下議題 + 主題素材庫（Layer 2 SOFT 去重聚合）
 *
 * 對應 8 步價值鏈第 4 步「主題」聚合視角。
 * 主題素材庫 = 跨旗下議題自動 distinct，同 hash 多筆只呈現分數最高那筆。
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Stack, Tabs, Tab, Button, Chip, IconButton, Tooltip, Typography,
  Paper, CircularProgress, Checkbox, Radio,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import {
  ArrowBack as BackIcon, OpenInNew as OpenIcon,
  Refresh as RefreshIcon, TrackChanges as TargetIcon,
  ContentCopy as CopyIcon, Settings as ManageIcon,
} from '@mui/icons-material';

import FunctionPageHeader from '../components/FunctionPageHeader';
import SubjectAudiencesTab from '../components/research/SubjectAudiencesTab';
import { useMessage } from '../contexts/MessageContext';
import { logView, logUpdate } from '../utils/userLogHelper';
import { usePermission } from '../hooks/usePermission';
import { Topic, Subject, SubjectAggregateItem } from '../types/research';
import {
  getSubject, listSubjectTopics, listSubjectItems, listTopics,
  replaceSubjectTopics,
} from '../services/researchService';
import { formatLocal } from '../utils/dateHelper';

const FUNC_CODE = 'ee_research_subjects';
const fmt = (iso?: string | null) => iso ? formatLocal(iso) : '—';

const ResearchSubjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showApiError } = useMessage();
  const { hasPermission } = usePermission();
  const canManage = hasPermission('ee_research_subjects', 'update');

  const [subject, setSubject] = useState<Subject | null>(null);
  const [tab, setTab] = useState<'topics' | 'items' | 'audiences'>('topics');
  const [topics, setTopics] = useState<Topic[]>([]);
  const [items, setItems] = useState<SubjectAggregateItem[]>([]);
  const [loading, setLoading] = useState(false);

  // 管理旗下議題 picker 狀態
  const [manageOpen, setManageOpen] = useState(false);
  const [allTopics, setAllTopics] = useState<Topic[]>([]);
  const [pickedIds, setPickedIds] = useState<number[]>([]);
  const [primaryIds, setPrimaryIds] = useState<number[]>([]);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSaving, setPickerSaving] = useState(false);

  const subjectId = id ? Number(id) : null;

  const reloadSubject = useCallback(async () => {
    if (!subjectId) return;
    try {
      const s = await getSubject(subjectId);
      setSubject(s);
    } catch (err: any) {
      showApiError(err);
    }
  }, [subjectId, showApiError]);

  const reloadData = useCallback(async () => {
    if (!subjectId) return;
    if (tab === 'audiences') return;  // audiences tab 自管 reload
    setLoading(true);
    try {
      if (tab === 'topics') {
        const t = await listSubjectTopics(subjectId);
        setTopics(t);
        logView(FUNC_CODE, { tab: 'topics', subject_id: subjectId, count: t.length });
      } else if (tab === 'items') {
        const its = await listSubjectItems(subjectId, { page: 1, page_size: 200 });
        setItems(its);
        logView(FUNC_CODE, { tab: 'items', subject_id: subjectId, count: its.length });
      }
    } catch (err: any) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [subjectId, tab, showApiError]);

  useEffect(() => { reloadSubject(); }, [reloadSubject]);
  useEffect(() => { reloadData(); }, [reloadData]);

  const openManage = async () => {
    if (!subjectId) return;
    setManageOpen(true);
    setPickerSearch('');
    try {
      // 抓所有 active 議題（不限本主題下的）
      const all = await listTopics({ is_active: true });
      setAllTopics(all);
      // 既有勾選 = 目前掛在本主題下的議題
      const linkedIds = topics.map(t => t.id);
      setPickedIds(linkedIds);
      // 既有主場 = 目前在本主題下標 is_primary 的議題
      const primary = topics
        .filter(t => t.subjects?.find(s => s.id === subjectId)?.is_primary)
        .map(t => t.id);
      setPrimaryIds(primary);
    } catch (err: any) {
      showApiError(err);
    }
  };

  const handleManageSave = async () => {
    if (!subjectId) return;
    setPickerSaving(true);
    try {
      await replaceSubjectTopics(subjectId, pickedIds, primaryIds);
      logUpdate(FUNC_CODE,
        { action: 'replace_subject_topics_request',
          subject_id: subjectId,
          picked: pickedIds, primary: primaryIds },
        { ok: true });
      showSuccess('SYS020002', { name: subject?.subject_name || '主題',
                                 id: String(subjectId) });
      setManageOpen(false);
      reloadData();
    } catch (err: any) {
      logUpdate(FUNC_CODE,
        { action: 'replace_subject_topics_request',
          subject_id: subjectId, picked: pickedIds, primary: primaryIds },
        {}, String(err?.message || err));
      showApiError(err);
    } finally {
      setPickerSaving(false);
    }
  };

  const togglePicked = (tid: number) => {
    setPickedIds(prev => {
      const has = prev.includes(tid);
      if (has) {
        // 取消勾選同時取消主場
        setPrimaryIds(p => p.filter(id => id !== tid));
        return prev.filter(id => id !== tid);
      }
      return [...prev, tid];
    });
  };

  const togglePrimary = (tid: number) => {
    if (!pickedIds.includes(tid)) return;
    setPrimaryIds(prev => prev.includes(tid)
      ? prev.filter(id => id !== tid)
      : [...prev, tid]);
  };

  const filteredAllTopics = allTopics.filter(t => {
    if (!pickerSearch) return true;
    const q = pickerSearch.toLowerCase();
    return t.topic_name.toLowerCase().includes(q)
      || t.topic_code.toLowerCase().includes(q);
  });

  if (!subjectId) {
    return <Typography>主題 ID 無效</Typography>;
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }} spacing={1}>
        <IconButton onClick={() => navigate('/ee_research_subjects')}>
          <BackIcon />
        </IconButton>
        <FunctionPageHeader funcCode={FUNC_CODE} fallback="主題詳細" />
        {subject && (
          <Chip
            icon={<TargetIcon />}
            label={`${subject.subject_name}（${subject.subject_code}）`}
            color="primary"
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        {canManage && tab === 'topics' && (
          <Button
            size="small" variant="contained"
            startIcon={<ManageIcon />}
            onClick={openManage}
          >管理旗下議題</Button>
        )}
        <Button
          size="small" variant="outlined"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={reloadData} disabled={loading}
        >重新整理</Button>
      </Stack>

      {subject?.description && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
          <Typography variant="body2" color="text.secondary">
            {subject.description}
          </Typography>
        </Paper>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="topics" label={`旗下議題（${topics.length || 0}）`} />
        <Tab value="audiences" label="對象與誘因" />
        <Tab value="items" label={`主題素材庫（${items.length || 0}）`} />
      </Tabs>

      {tab === 'audiences' && subject && (
        <SubjectAudiencesTab
          subjectId={subjectId}
          subjectName={subject.subject_name}
          canManage={canManage}
        />
      )}

      {tab === 'topics' && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell width={120}>代碼</TableCell>
                <TableCell>名稱</TableCell>
                <TableCell width={100}>主場</TableCell>
                <TableCell width={170}>更新時間</TableCell>
                <TableCell width={70} align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topics.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center"
                             sx={{ py: 4, color: 'text.secondary' }}>
                    {loading ? '載入中…' : '尚未掛任何議題到此主題'}
                  </TableCell>
                </TableRow>
              ) : (
                topics.map(t => {
                  const myLink = t.subjects?.find(s => s.id === subjectId);
                  const isPrimary = !!myLink?.is_primary;
                  return (
                    <TableRow key={t.id} hover>
                      <TableCell>
                        <Typography variant="body2"
                                    sx={{ fontFamily: 'monospace' }}>
                          {t.topic_code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {t.topic_name}
                        </Typography>
                        {t.description && (
                          <Typography variant="caption" color="text.secondary"
                                      sx={{ display: 'block' }}>
                            {t.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {isPrimary
                          ? <Chip size="small" label="主場" color="primary" />
                          : <Chip size="small" label="借用" variant="outlined" />}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{fmt(t.updated_at)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="進入議題工作區">
                          <IconButton size="small" color="primary"
                                      onClick={() => navigate(`/ee_research_topics/${t.topic_code}`)}>
                            <OpenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 'items' && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell width={50} align="center">#</TableCell>
                <TableCell>標題 / 摘要</TableCell>
                <TableCell width={180}>來源議題</TableCell>
                <TableCell width={100}>類型</TableCell>
                <TableCell width={80} align="right">分數</TableCell>
                <TableCell width={80}>狀態</TableCell>
                <TableCell width={170}>建立時間</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center"
                             sx={{ py: 4, color: 'text.secondary' }}>
                    {loading ? '載入中…' : '尚無素材；先到旗下議題蒐集 / 上傳'}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((it, idx) => (
                  <TableRow key={it.id} hover>
                    <TableCell align="center">
                      <Typography variant="caption" color="text.secondary">
                        {idx + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {it.title}
                      </Typography>
                      {it.ai_summary && (
                        <Typography variant="caption" color="text.secondary"
                                    sx={{ display: 'block',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          maxWidth: 600 }}>
                          {it.ai_summary}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`點擊跳到議題：${it.source_topic_name}`}>
                        <Chip size="small"
                          label={it.source_topic_name}
                          clickable
                          variant="outlined"
                          onClick={() => navigate(`/ee_research_topics/${it.source_topic_code}`)}
                        />
                      </Tooltip>
                      {it.duplicate_count > 0 && (
                        <Tooltip title={`此素材在主題下另外 ${it.duplicate_count} 個議題也有重複（已自動去重）`}>
                          <Chip size="small"
                            icon={<CopyIcon sx={{ fontSize: 14 }} />}
                            label={`+${it.duplicate_count}`}
                            color="default"
                            sx={{ ml: 0.5 }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={it.item_type} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">
                        {it.ai_relevance_score != null
                          ? it.ai_relevance_score.toFixed(2)
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={it.ai_status}
                        color={
                          it.ai_status === 'done' ? 'success' :
                          it.ai_status === 'failed' ? 'error' :
                          it.ai_status === 'processing' ? 'warning' : 'default'
                        } />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{fmt(it.created_at)}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 管理旗下議題 picker dialog（subject-led：從議題池子挑 + 主場 radio） */}
      <Dialog open={manageOpen} onClose={() => setManageOpen(false)}
              maxWidth="md" fullWidth>
        <DialogTitle>
          管理旗下議題 — {subject?.subject_name}
          <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}>
            從議題池子勾選；勾起來表示此主題引用該議題。「主場」radio 標示「此議題以本主題為主場」（每議題最多一個主場）。
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            size="small" fullWidth placeholder="🔍 搜尋議題代碼或名稱..."
            value={pickerSearch}
            onChange={e => setPickerSearch(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell width={50} align="center">納入</TableCell>
                  <TableCell width={120}>代碼</TableCell>
                  <TableCell>名稱</TableCell>
                  <TableCell width={150}>目前歸屬</TableCell>
                  <TableCell width={70} align="center">主場</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAllTopics.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center"
                               sx={{ py: 3, color: 'text.secondary' }}>
                      議題池子是空的（先到「議題管理」建立議題）
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAllTopics.map(t => {
                    const checked = pickedIds.includes(t.id);
                    const isPrimaryHere = primaryIds.includes(t.id);
                    const otherPrimary = t.subjects?.find(
                      s => s.is_primary && s.id !== subjectId,
                    );
                    return (
                      <TableRow key={t.id} hover
                                sx={{ bgcolor: checked ? 'action.selected' : 'inherit' }}>
                        <TableCell align="center" padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={checked}
                            onChange={() => togglePicked(t.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2"
                                      sx={{ fontFamily: 'monospace' }}>
                            {t.topic_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {t.topic_name}
                          </Typography>
                          {t.description && (
                            <Typography variant="caption" color="text.secondary"
                                        sx={{ display: 'block',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              maxWidth: 380 }}>
                              {t.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {(t.subjects || []).map(s => (
                              <Chip key={s.id} size="small"
                                label={s.subject_name}
                                color={s.id === subjectId ? 'primary'
                                  : s.is_primary ? 'default' : 'default'}
                                variant={s.is_primary ? 'filled' : 'outlined'}
                                icon={s.is_primary ? <span>🏠</span> : undefined}
                              />
                            ))}
                            {(!t.subjects || t.subjects.length === 0) && (
                              <Typography variant="caption" color="text.disabled">
                                獨立議題
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={
                            !checked ? '需先勾選納入才能設主場'
                            : otherPrimary && !isPrimaryHere
                              ? `目前主場在「${otherPrimary.subject_name}」— 點擊會切過來`
                              : isPrimaryHere ? '本主題為此議題的主場' : '設為主場'
                          }>
                            <span>
                              <Radio
                                size="small"
                                checked={isPrimaryHere}
                                disabled={!checked}
                                onClick={() => togglePrimary(t.id)}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              已勾選：<strong>{pickedIds.length}</strong> 個議題
              ｜本主題為主場：<strong>{primaryIds.length}</strong>
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageOpen(false)}>取消</Button>
          <Button onClick={handleManageSave} variant="contained"
                  disabled={pickerSaving}>
            {pickerSaving ? <CircularProgress size={18} /> : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResearchSubjectDetailPage;